import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpEvent } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import { PublicFormRenderModel } from "../models/public-form.model";

export interface PublicFileUploadResponse {
  fileId: number;
  originalName: string;
  contentType: string;
  fileSize: number;
  publicUrl: string;
  isPublic: boolean;
}

export interface FormFieldValue {
  fieldKey: string;
  rawValue: string | null;
  selectedOptionId: number | null;
}

export interface PublicFormViewPayload {
  visitorToken?: string;
  pageUrl?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

export interface PublicFormSubmitPayload {
  visitorToken?: string;
  pageUrl?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  fields: FormFieldValue[];
}

export interface PublicFormSubmitResponse {
  submissionId: number;
  status: string;
  postSubmitAction: 'MESSAGE' | 'REDIRECT' | 'BOTH' | null;
  postSubmitMessage?: string;
  postSubmitRedirectUrl?: string;
  postSubmitRedirectDelayS?: number;
}

@Injectable({ providedIn: 'root' })
export class PublicFormService {
  private http = inject(HttpClient);
  private readonly publicUrl = `${environment.apiUrl}api/public/forms`;

  getFormBySlug(slug: string): Observable<PublicFormRenderModel> {
    return this.http.get<PublicFormRenderModel>(`${this.publicUrl}/${slug}`);
  }

  recordView(formId: number, payload: PublicFormViewPayload): Observable<void> {
    return this.http.post<void>(`${this.publicUrl}/${formId}/views`, payload);
  }

  submitForm(formId: number, payload: PublicFormSubmitPayload): Observable<PublicFormSubmitResponse> {
    return this.http.post<PublicFormSubmitResponse>(`${this.publicUrl}/${formId}/submit`, payload);
  }

  /**
   * Sube un adjunto de un formulario público.
   *
   * Devuelve el flujo de HttpEvent (no solo el body) a propósito: con `reportProgress`
   * el llamador puede pintar el porcentaje y, sobre todo, aplicar un timeout de
   * INACTIVIDAD (`timeout({ each })`) que se reinicia con cada evento de progreso. Sin
   * eventos no hay forma de distinguir "va lento" de "se colgó", y una subida que se
   * queda a medias (proxy que corta, red que se cae) dejaba el campo en «Subiendo
   * archivo...» para siempre, sin error y sin poder reintentar.
   */
  uploadFile(formId: number, file: File): Observable<HttpEvent<PublicFileUploadResponse>> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<PublicFileUploadResponse>(`${this.publicUrl}/${formId}/upload`, form, {
      reportProgress: true,
      observe: 'events',
    });
  }
}
